import { Close, DeleteOutline, Send, WarningAmberOutlined } from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  TableContainer,
  TextField,
  Typography,
} from "@mui/material";
import { useState, useContext } from "react";
import { useOktaAuth } from "@okta/okta-react";
import { ModuleContext } from "../../context/ModuleContext";
import { AlternateAliasContext } from "../../context/AlternateAliasContext";

const WSU_RED = "#A60F2D";

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    "&:hover fieldset": { borderColor: WSU_RED },
    "&.Mui-focused fieldset": { borderColor: WSU_RED },
  },
  "& .MuiInputLabel-root.Mui-focused": { color: WSU_RED },
};

export const DeleteAlias = () => {
  const { setModule } = useContext(ModuleContext);
  const { aliasModule, setAliasModule } = useContext(AlternateAliasContext);
  const { authState } = useOktaAuth();

  const [state, setState] = useState({ error: null, waiting: false, comment: "", message: null });

  const deleteAlias = async () => {
    setState((prev) => ({ ...prev, waiting: true }));

    const deleteUrl = new URL(`${process.env.REACT_APP_AWS_API_GATEWAY_ENDPOINT}/alias/delete`);
    deleteUrl.searchParams.append("dn", aliasModule.dn);
    deleteUrl.searchParams.append("comment", state.comment);
    deleteUrl.searchParams.append("callerId", authState.accessToken.claims.sub);
    deleteUrl.searchParams.append("upn", aliasModule.upn);

    try {
      const res = await fetch(deleteUrl.toString(), {
        method: "POST",
        headers: {
          Authorization: authState.accessToken.accessToken,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      });

      if (!res.ok) {
        const text = await res.text();
        const errorObj = JSON.parse(text);
        throw new Error(errorObj.error.message);
      }

      await res.json();
      setState((prev) => ({ ...prev, waiting: false, error: null, message: "Alternate Alias Deleted" }));
    } catch (err) {
      setState((prev) => ({ ...prev, waiting: false, error: { message: err.message } }));
    }
  };

  const closeContainer = () => {
    setModule((prev) => ({ ...prev, isVisible: false }));
    const updatedResults = aliasModule.searchResults.filter(
      (result) => result.userPrincipalName !== aliasModule.upn
    );
    setAliasModule((prev) => ({ ...prev, isOpen: false, searchResults: updatedResults }));
  };

  if (state.waiting) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
        <CircularProgress style={{ color: WSU_RED }} size={80} thickness={5} />
      </Box>
    );
  }

  if (state.message) {
    return (
      <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2, overflow: "hidden" }}>
        <Box sx={{ backgroundColor: "#2e7d32", color: "white", px: 3, py: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
          <DeleteOutline fontSize="small" />
          <Typography variant="subtitle2" fontWeight={700}>Alias Deleted</Typography>
        </Box>
        <Box sx={{ px: 3, pt: 3, pb: 1 }}>
          <Typography variant="overline" sx={{ color: WSU_RED, fontWeight: 700, letterSpacing: 1.5 }}>
            Confirmation
          </Typography>
        </Box>
        <Box sx={{ px: 3, py: 2 }}>
          <Typography variant="body2" color="text.secondary">{state.message}</Typography>
        </Box>
        <Box sx={{ px: 3, pb: 4, display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="outlined"
            endIcon={<Close />}
            onClick={closeContainer}
            sx={{ borderColor: "#ccc", color: "#666", "&:hover": { borderColor: "#aaa", backgroundColor: "#f9f9f9" } }}
          >
            Close
          </Button>
        </Box>
      </TableContainer>
    );
  }

  return (
    <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2, overflow: "hidden" }}>

      {/* Error Banner */}
      {state.error && (
        <Box sx={{ backgroundColor: WSU_RED, color: "white", px: 3, py: 2 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>Delete failed</Typography>
          <Typography variant="body2">{state.error.message}</Typography>
        </Box>
      )}

      {/* Warning Banner */}
      <Box sx={{ backgroundColor: "#fff3e0", px: 3, py: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
        <WarningAmberOutlined sx={{ color: "#e65100" }} fontSize="small" />
        <Typography variant="body2" sx={{ color: "#e65100", fontWeight: 600 }}>
          This action is permanent and cannot be undone.
        </Typography>
      </Box>

      {/* ── Section: Confirm Deletion ── */}
      <Box sx={{ px: 3, pt: 3, pb: 1 }}>
        <Typography variant="overline" sx={{ color: WSU_RED, fontWeight: 700, letterSpacing: 1.5 }}>
          Confirm Deletion
        </Typography>
        <Typography variant="caption" sx={{ display: "block", color: "#777", mt: 0.25 }}>
          {aliasModule.upn}
        </Typography>
      </Box>

      <Divider sx={{ mx: 3 }} />

      {/* ── Section: Justification ── */}
      <Box sx={{ px: 3, pt: 3, pb: 1 }}>
        <Typography variant="overline" sx={{ color: WSU_RED, fontWeight: 700, letterSpacing: 1.5 }}>
          Justification
        </Typography>
      </Box>

      <Box sx={{ px: 3, pb: 3 }}>
        <TextField
          required
          fullWidth
          size="small"
          multiline
          rows={3}
          label="Comment"
          value={state.comment}
          onChange={(e) => setState((prev) => ({ ...prev, comment: e.target.value }))}
          placeholder="Provide a reason for deleting this alias"
          sx={fieldSx}
        />
      </Box>

      {/* ── Actions ── */}
      <Box sx={{ px: 3, pb: 4, display: "flex", justifyContent: "flex-end", gap: 2 }}>
        <Button
          variant="outlined"
          endIcon={<Close />}
          onClick={() => setModule((prev) => ({ ...prev, isVisible: false }))}
          sx={{ borderColor: "#ccc", color: "#666", "&:hover": { borderColor: "#aaa", backgroundColor: "#f9f9f9" } }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          endIcon={<Send />}
          onClick={deleteAlias}
          disabled={!state.comment.trim()}
          sx={{
            backgroundColor: WSU_RED,
            color: "white",
            px: 4,
            py: 1.25,
            fontWeight: 600,
            letterSpacing: 0.5,
            "&:hover": { backgroundColor: "#8a0c25" },
            "&.Mui-disabled": { backgroundColor: "#ddd", color: "#aaa" },
          }}
        >
          Delete Alias
        </Button>
      </Box>

    </TableContainer>
  );
};
